/**
 * The binding language of interactive templates (wayai-pro/wayai#4751 decision 12): the ONE evaluator
 * web, mobile and the backend share. It fills a template's text with values (`{{item.name}}`), repeats
 * (`{{#each items}}…{{/each}}`), branches on truthiness (`{{#if x}}…{{else}}…{{/if}}`) and formats
 * through a fixed formatter list (`{{price | currency}}`). Nothing else: no expressions, no loops
 * beyond `each`, no functions, no user code (`sum`, arithmetic, comparisons and local inputs are
 * Phase 1b's). Every entry point is total: bad input renders empty text or the raw value, never a throw.
 *
 * GRAMMAR
 *   text      := (literal | '{{' tag '}}')*
 *   tag       := expr | '#each ' path | '#if ' path | 'else' | '/each' | '/if'
 *   expr      := path ('|' formatter (':' arg)?)*
 *   path      := root ('.' segment)*        — segments are names or array indices (`items.0.name`)
 *   arg       := a quoted string ("…" or '…') or a bare word (`BRL`, `2`)
 * A path names one of these roots:
 *   - `params.<name>`  a value the message was sent with (declared in the template's `params`);
 *   - `state.<slug>`   a hub state's value (`state.order.items`);
 *   - `data.<field>`   a field of the template's data snapshot; a bare `<field>` is the same thing;
 *   - `form.<name>`    a submitted form's value, only in the action a form submits;
 *   - inside `each`: `item` (or `this`) the current element, `@index` (0-based), `@n` (1-based),
 *     `@first`, `@last`, and a bare name, which reads the current element's field first and the data
 *     snapshot's second. Nested `each` sees only its own element.
 * Lookups read own properties of plain objects and arrays only; anything else is missing.
 *
 * VALUES. In text, a string renders as is, a finite number as its digits, a boolean as `true`/`false`,
 * an array of scalars joined by ", ", and anything else (missing, an object) as nothing. A value
 * template (`resolveBindingValue`) keeps types: a string that is exactly one `{{expr}}` yields the
 * value itself (a number stays a number); any other string renders as text; objects and arrays
 * resolve field by field.
 *
 * TRUTHINESS (`#if`, a block's `if`): false for missing, null, false, 0, NaN, "", and an empty array;
 * true otherwise (an empty object included).
 */

/** The most a `{{#each}}` (or a `list` block) repeats, whatever its list holds. */
export const BINDING_EACH_MAX_ITEMS = 100;
/** The most `each` levels one another may sit in. */
export const BINDING_EACH_MAX_DEPTH = 3;
/**
 * The most rendered text reaches, in UTF-16 code units (at most 48 KiB of UTF-8); the rest is cut. A
 * template message's `message_text`, its rendered fallback, is bounded by it.
 */
export const BINDING_TEXT_MAX_OUTPUT = 16 * 1024;
/** The most segments one path has. */
export const BINDING_PATH_MAX_SEGMENTS = 12;
/** The most formatters one `{{expr}}` chains. */
export const BINDING_MAX_FORMATTERS = 4;
/** The most levels a value template nests (objects and arrays). */
export const BINDING_VALUE_MAX_DEPTH = 8;
/**
 * The most steps (a node visited, an `each` pass, a whole binding read) one render takes. Output
 * alone bounds nothing: `each` inside `each` over a body that prints nothing runs 100^3 times. A
 * render that spends it stops where it is.
 */
export const BINDING_RENDER_MAX_STEPS = 50_000;

/** The formatters, a fixed list (decision 12). */
export const BINDING_FORMATTERS = [
  'currency',
  'number',
  'percent',
  'date',
  'datetime',
  'time',
  'count',
  'upper',
  'lower',
  'truncate',
  'default',
  'join',
] as const;
export type BindingFormatterName = (typeof BINDING_FORMATTERS)[number];

/** The roots a path may start with besides a bare data field (and the loop names inside `each`). */
export const BINDING_ROOTS = ['params', 'state', 'data', 'form', 'item', 'this'] as const;

/** The names `each` gives its position (only inside an `each`). */
export const BINDING_LOOP_NAMES = ['@index', '@n', '@first', '@last'] as const;
/** A path segment that names a field (a data field among them). */
export const BINDING_NAME_SEGMENT_REGEX = /^[A-Za-z_][A-Za-z0-9_-]*$/;
const LOOP_NAMES = BINDING_LOOP_NAMES;
const NAME_SEGMENT = BINDING_NAME_SEGMENT_REGEX;
const INDEX_SEGMENT = /^\d{1,6}$/;
const DATE_STYLES = ['short', 'medium', 'long'] as const;
const CURRENCY_CODE = /^[A-Z]{3}$/;

// =============================================================================
// PARSING
// =============================================================================

export interface BindingFormatterCall {
  name: string;
  arg: string | null;
}

export interface BindingExpr {
  path: string[];
  formatters: BindingFormatterCall[];
}

export type BindingNode =
  | { t: 'text'; value: string }
  | { t: 'var'; expr: BindingExpr }
  | { t: 'each'; path: string[]; body: BindingNode[] }
  | { t: 'if'; path: string[]; then: BindingNode[]; else: BindingNode[] };

export type BindingParse<T> = { ok: true; value: T } | { ok: false; error: string };

/** Splits a path into segments and checks each: `item.price`, `@n`, `state.order.items.0`. */
export function parseBindingPath(source: string): BindingParse<string[]> {
  const text = source.trim();
  if (!text) return { ok: false, error: 'empty path' };
  if ((LOOP_NAMES as readonly string[]).includes(text)) return { ok: true, value: [text] };
  const segments = text.split('.');
  if (segments.length > BINDING_PATH_MAX_SEGMENTS) {
    return { ok: false, error: `path "${text}" has more than ${BINDING_PATH_MAX_SEGMENTS} segments` };
  }
  for (const [i, segment] of segments.entries()) {
    const valid = NAME_SEGMENT.test(segment) || (i > 0 && INDEX_SEGMENT.test(segment));
    if (!valid) return { ok: false, error: `path "${text}" has an invalid segment "${segment}"` };
  }
  return { ok: true, value: segments };
}

/** Reads a quoted string or bare word from `rest`, starting at its first character. */
function readArg(rest: string): BindingParse<{ arg: string; length: number }> {
  const quote = rest[0];
  if (quote === '"' || quote === "'") {
    const end = rest.indexOf(quote, 1);
    if (end < 0) return { ok: false, error: 'unterminated quoted argument' };
    return { ok: true, value: { arg: rest.slice(1, end), length: end + 1 } };
  }
  const match = /^[^\s|]+/.exec(rest);
  if (!match) return { ok: false, error: 'missing formatter argument' };
  return { ok: true, value: { arg: match[0], length: match[0].length } };
}

/** Parses `path | formatter: arg | formatter`. */
export function parseBindingExpr(source: string): BindingParse<BindingExpr> {
  const pipe = source.indexOf('|');
  const pathText = pipe < 0 ? source : source.slice(0, pipe);
  const path = parseBindingPath(pathText);
  if (!path.ok) return path;
  const formatters: BindingFormatterCall[] = [];
  let rest = pipe < 0 ? '' : source.slice(pipe);
  while (rest.trim()) {
    rest = rest.trimStart();
    if (rest[0] !== '|') return { ok: false, error: `unexpected "${rest.slice(0, 20)}"` };
    rest = rest.slice(1).trimStart();
    const name = /^[a-z_]+/.exec(rest);
    if (!name) return { ok: false, error: 'missing formatter name after "|"' };
    rest = rest.slice(name[0].length).trimStart();
    let arg: string | null = null;
    if (rest[0] === ':') {
      rest = rest.slice(1).trimStart();
      const read = readArg(rest);
      if (!read.ok) return read;
      arg = read.value.arg;
      rest = rest.slice(read.value.length);
    }
    formatters.push({ name: name[0], arg });
    if (formatters.length > BINDING_MAX_FORMATTERS) {
      return { ok: false, error: `more than ${BINDING_MAX_FORMATTERS} formatters` };
    }
  }
  return { ok: true, value: { path: path.value, formatters } };
}

type OpenBlock =
  | { kind: 'each'; path: string[]; body: BindingNode[] }
  | { kind: 'if'; path: string[]; then: BindingNode[]; else: BindingNode[]; inElse: boolean };

const PARSE_CACHE_MAX = 1000;
const parseCache = new Map<string, BindingParse<BindingNode[]>>();

/**
 * Parses a text template into nodes, or says what is wrong with it. Results are cached per source
 * (renders re-read the same template strings for every list element), so callers never mutate them.
 */
export function parseBindingText(source: string): BindingParse<BindingNode[]> {
  const cached = parseCache.get(source);
  if (cached) return cached;
  const parsed = parseBindingTextUncached(source);
  if (parseCache.size >= PARSE_CACHE_MAX) parseCache.clear();
  parseCache.set(source, parsed);
  return parsed;
}

function parseBindingTextUncached(source: string): BindingParse<BindingNode[]> {
  const root: BindingNode[] = [];
  const stack: OpenBlock[] = [];
  const target = (): BindingNode[] => {
    const top = stack[stack.length - 1];
    if (!top) return root;
    if (top.kind === 'each') return top.body;
    return top.inElse ? top.else : top.then;
  };
  let eachDepth = 0;
  let at = 0;
  while (at < source.length) {
    const open = source.indexOf('{{', at);
    if (open < 0) {
      target().push({ t: 'text', value: source.slice(at) });
      break;
    }
    if (open > at) target().push({ t: 'text', value: source.slice(at, open) });
    const close = source.indexOf('}}', open + 2);
    if (close < 0) return { ok: false, error: 'a "{{" is never closed by "}}"' };
    const tag = source.slice(open + 2, close).trim();
    at = close + 2;
    if (tag.startsWith('#each ') || tag === '#each') {
      const path = parseBindingPath(tag.slice(5));
      if (!path.ok) return { ok: false, error: `{{#each}}: ${path.error}` };
      eachDepth += 1;
      if (eachDepth > BINDING_EACH_MAX_DEPTH) {
        return { ok: false, error: `{{#each}} nests more than ${BINDING_EACH_MAX_DEPTH} levels` };
      }
      stack.push({ kind: 'each', path: path.value, body: [] });
    } else if (tag.startsWith('#if ') || tag === '#if') {
      const path = parseBindingPath(tag.slice(3));
      if (!path.ok) return { ok: false, error: `{{#if}}: ${path.error}` };
      stack.push({ kind: 'if', path: path.value, then: [], else: [], inElse: false });
    } else if (tag === 'else') {
      const top = stack[stack.length - 1];
      if (!top || top.kind !== 'if' || top.inElse) return { ok: false, error: '{{else}} outside an {{#if}}' };
      top.inElse = true;
    } else if (tag === '/each' || tag === '/if') {
      const top = stack.pop();
      const kind = tag.slice(1);
      if (!top || top.kind !== kind) return { ok: false, error: `{{${tag}}} closes no {{#${kind}}}` };
      if (top.kind === 'each') {
        eachDepth -= 1;
        target().push({ t: 'each', path: top.path, body: top.body });
      } else {
        target().push({ t: 'if', path: top.path, then: top.then, else: top.else });
      }
    } else if (tag.startsWith('#') || tag.startsWith('/')) {
      return { ok: false, error: `unknown block "{{${tag}}}": only #each and #if exist` };
    } else {
      const expr = parseBindingExpr(tag);
      if (!expr.ok) return { ok: false, error: `{{${tag}}}: ${expr.error}` };
      target().push({ t: 'var', expr: expr.value });
    }
  }
  const unclosed = stack[stack.length - 1];
  if (unclosed) return { ok: false, error: `{{#${unclosed.kind}}} is never closed` };
  return { ok: true, value: root };
}

/** The expression of a string that is exactly one `{{expr}}`, or null. */
export function wholeBindingExpr(source: string): BindingExpr | null {
  if (!source.startsWith('{{') || !source.endsWith('}}')) return null;
  const inner = source.slice(2, -2);
  if (inner.includes('{{') || inner.includes('}}')) return null;
  const tag = inner.trim();
  if (tag.startsWith('#') || tag.startsWith('/') || tag === 'else') return null;
  const expr = parseBindingExpr(tag);
  return expr.ok ? expr.value : null;
}

// =============================================================================
// EVALUATION
// =============================================================================

/** How values format: the hub's locale, currency and time zone. All optional. */
export interface BindingFormatOptions {
  /** BCP 47 tag (`pt-BR`). Unset: the runtime's default. */
  locale?: string;
  /** ISO 4217 code `currency` uses when not given one (`BRL`). Unset: a plain two-decimal number. */
  currency?: string;
  /** IANA zone dates format in (`America/Sao_Paulo`). Unset: the runtime's. */
  timeZone?: string;
}

/** What bindings read. Every field is optional: what is absent reads as missing. */
export interface BindingContext {
  params?: unknown;
  data?: unknown;
  state?: unknown;
  /** The values a form submitted: only the action a form submits reads them. */
  form?: unknown;
  format?: BindingFormatOptions;
}

/** One `each` level: its element and position. */
export interface BindingFrame {
  item: unknown;
  index: number;
  length: number;
}

/** The steps a render may still take (`BINDING_RENDER_MAX_STEPS`); shared by everything one render resolves. */
export interface BindingBudget {
  left: number;
}

/** A context plus the `each` levels a binding sits in, innermost last, and the render's step budget. */
export interface BindingScope {
  context: BindingContext;
  frames: readonly BindingFrame[];
  /** Unset: each render or value resolution takes a fresh `BINDING_RENDER_MAX_STEPS`. */
  budget?: BindingBudget;
}

export function bindingScope(
  context: BindingContext,
  frames: readonly BindingFrame[] = [],
  budget?: BindingBudget,
): BindingScope {
  return budget ? { context, frames, budget } : { context, frames };
}

function withBudget(scope: BindingScope): Required<BindingScope> {
  return { ...scope, budget: scope.budget ?? { left: BINDING_RENDER_MAX_STEPS } };
}

/** A plain object (not null, not an array): what a binding reads fields of. */
export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** An own property of a plain object or array, or undefined. */
function own(value: unknown, key: string): unknown {
  if (Array.isArray(value)) {
    if (!INDEX_SEGMENT.test(key)) return undefined;
    const i = Number(key);
    return i < value.length ? value[i] : undefined;
  }
  if (isPlainRecord(value) && Object.prototype.hasOwnProperty.call(value, key)) return value[key];
  return undefined;
}

/** The value a parsed path names in `scope`; undefined when missing. */
export function evaluateBindingPath(path: readonly string[], scope: BindingScope): unknown {
  try {
    const [head, ...rest] = path;
    if (head === undefined) return undefined;
    const frame = scope.frames[scope.frames.length - 1];
    let value: unknown;
    switch (head) {
      case '@index':
        return frame ? frame.index : undefined;
      case '@n':
        return frame ? frame.index + 1 : undefined;
      case '@first':
        return frame ? frame.index === 0 : undefined;
      case '@last':
        return frame ? frame.index === frame.length - 1 : undefined;
      case 'item':
      case 'this':
        value = frame?.item;
        break;
      case 'params':
      case 'state':
      case 'data':
      case 'form':
        value = scope.context[head];
        break;
      default: {
        const fromItem = frame ? own(frame.item, head) : undefined;
        value = fromItem !== undefined ? fromItem : own(scope.context.data, head);
      }
    }
    for (const segment of rest) {
      value = own(value, segment);
      if (value === undefined) return undefined;
    }
    return value;
  } catch {
    return undefined;
  }
}

/** Truthiness as `#if` and a block's `if` read it. */
export function isBindingTruthy(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') return value !== 0 && !Number.isNaN(value);
  return value !== undefined && value !== null && value !== false && value !== '';
}

function isScalar(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** A value as text shows it (VALUES above). */
export function bindingValueText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value) && value.every(isScalar)) return value.map(bindingValueText).join(', ');
  return '';
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toDate(value: unknown): Date | null {
  const date = typeof value === 'number' ? new Date(value) : typeof value === 'string' ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date : null;
}

function decimalsArg(arg: string | null, fallback: number): number | null {
  if (arg === null) return fallback;
  if (!/^\d$/.test(arg)) return null;
  const n = Number(arg);
  return n <= 6 ? n : null;
}

const DATE_PARTS: Record<(typeof DATE_STYLES)[number], Intl.DateTimeFormatOptions> = {
  short: { year: 'numeric', month: '2-digit', day: '2-digit' },
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
  long: { year: 'numeric', month: 'long', day: 'numeric' },
};
const TIME_PARTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

function dateStyle(arg: string | null): (typeof DATE_STYLES)[number] | null {
  if (arg === null) return 'medium';
  return (DATE_STYLES as readonly string[]).includes(arg) ? (arg as (typeof DATE_STYLES)[number]) : null;
}

/** Why an argument is wrong for a formatter, or null when it is fine. The validator's check. */
export function bindingFormatterArgError(name: string, arg: string | null): string | null {
  switch (name) {
    case 'currency':
      return arg === null || CURRENCY_CODE.test(arg) ? null : 'currency takes an ISO 4217 code (e.g. BRL)';
    case 'number':
    case 'percent':
      return decimalsArg(arg, 0) === null ? `${name} takes a number of decimals from 0 to 6` : null;
    case 'date':
    case 'datetime':
      return dateStyle(arg) === null ? `${name} takes short, medium or long` : null;
    case 'time':
    case 'count':
    case 'upper':
    case 'lower':
      return arg === null ? null : `${name} takes no argument`;
    case 'truncate':
      return arg !== null && /^\d{1,4}$/.test(arg) && Number(arg) >= 1 ? null : 'truncate takes a length from 1 to 9999';
    case 'default':
      return arg === null ? 'default takes the text to show (e.g. default: "—")' : null;
    case 'join':
      return arg === null || arg.length <= 10 ? null : 'join takes a separator of at most 10 characters';
    default:
      return `unknown formatter "${name}": the formatters are ${BINDING_FORMATTERS.join(', ')}`;
  }
}

/**
 * Applies one formatter. A formatter that cannot apply (a wrong type, a bad argument, an unknown name,
 * an `Intl` refusal) gives back its input unchanged.
 */
export function applyBindingFormatter(
  call: BindingFormatterCall,
  value: unknown,
  options: BindingFormatOptions = {},
): unknown {
  try {
    if (bindingFormatterArgError(call.name, call.arg) !== null) return value;
    const { arg } = call;
    switch (call.name as BindingFormatterName) {
      case 'currency': {
        const n = toNumber(value);
        if (n === null) return value;
        const currency = arg ?? options.currency;
        if (!currency) {
          return new Intl.NumberFormat(options.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
        }
        return new Intl.NumberFormat(options.locale, { style: 'currency', currency }).format(n);
      }
      case 'number':
      case 'percent': {
        const n = toNumber(value);
        const digits = decimalsArg(arg, 0);
        if (n === null || digits === null) return value;
        return new Intl.NumberFormat(options.locale, {
          style: call.name === 'percent' ? 'percent' : 'decimal',
          minimumFractionDigits: arg === null ? 0 : digits,
          maximumFractionDigits: arg === null ? (call.name === 'percent' ? 0 : 3) : digits,
        }).format(n);
      }
      case 'date':
      case 'datetime':
      case 'time': {
        const date = toDate(value);
        if (!date) return value;
        const style = dateStyle(arg) ?? 'medium';
        const parts =
          call.name === 'time'
            ? TIME_PARTS
            : call.name === 'datetime'
              ? { ...DATE_PARTS[style], ...TIME_PARTS }
              : DATE_PARTS[style];
        return new Intl.DateTimeFormat(options.locale, { ...parts, timeZone: options.timeZone }).format(date);
      }
      case 'count':
        if (Array.isArray(value)) return value.length;
        return value === undefined || value === null ? 0 : value;
      case 'upper':
        return typeof value === 'string' ? value.toUpperCase() : value;
      case 'lower':
        return typeof value === 'string' ? value.toLowerCase() : value;
      case 'truncate': {
        if (typeof value !== 'string') return value;
        const max = Number(arg);
        return value.length > max ? `${value.slice(0, max)}…` : value;
      }
      case 'default':
        return isBindingTruthy(value) || value === 0 || value === false ? value : arg;
      case 'join':
        return Array.isArray(value) && value.every(isScalar) ? value.map(bindingValueText).join(arg ?? ', ') : value;
      default:
        return value;
    }
  } catch {
    return value;
  }
}

/** The value an expression gives: its path's value through each formatter in turn. */
export function evaluateBindingExpr(expr: BindingExpr, scope: BindingScope): unknown {
  let value = evaluateBindingPath(expr.path, scope);
  for (const call of expr.formatters) value = applyBindingFormatter(call, value, scope.context.format);
  return value;
}

/** The elements an `each` repeats over: an array's first `BINDING_EACH_MAX_ITEMS`, otherwise none. */
export function bindingEachItems(value: unknown, limit: number = BINDING_EACH_MAX_ITEMS): unknown[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, Math.max(0, Math.min(limit, BINDING_EACH_MAX_ITEMS)));
}

class TextSink {
  text = '';
  full = false;
  push(piece: string): void {
    if (this.full || !piece) return;
    const room = BINDING_TEXT_MAX_OUTPUT - this.text.length;
    if (piece.length >= room) {
      this.text += piece.slice(0, room);
      this.full = true;
    } else {
      this.text += piece;
    }
  }
}

function renderNodes(nodes: readonly BindingNode[], scope: Required<BindingScope>, sink: TextSink): void {
  const { budget } = scope;
  for (const node of nodes) {
    if (sink.full || budget.left <= 0) return;
    budget.left -= 1;
    switch (node.t) {
      case 'text':
        sink.push(node.value);
        break;
      case 'var':
        sink.push(bindingValueText(evaluateBindingExpr(node.expr, scope)));
        break;
      case 'if':
        renderNodes(isBindingTruthy(evaluateBindingPath(node.path, scope)) ? node.then : node.else, scope, sink);
        break;
      case 'each': {
        if (scope.frames.length >= BINDING_EACH_MAX_DEPTH) break;
        const items = bindingEachItems(evaluateBindingPath(node.path, scope));
        for (const [index, item] of items.entries()) {
          if (budget.left <= 0) return;
          budget.left -= 1;
          renderNodes(node.body, { context: scope.context, frames: [...scope.frames, { item, index, length: items.length }], budget }, sink);
        }
        break;
      }
    }
  }
}

/**
 * Renders a text template. A template that does not parse (which the validator refuses at write
 * time) renders as its own source. A render whose step budget runs out stops there. Never throws.
 */
export function renderBindingText(source: string, scope: BindingScope): string {
  try {
    const budgeted = withBudget(scope);
    if (budgeted.budget.left <= 0) return '';
    const parsed = parseBindingText(source);
    if (!parsed.ok) return source.slice(0, BINDING_TEXT_MAX_OUTPUT);
    const sink = new TextSink();
    renderNodes(parsed.value, budgeted, sink);
    return sink.text;
  } catch {
    return '';
  }
}

function resolveValue(template: unknown, scope: Required<BindingScope>, depth: number): unknown {
  if (typeof template === 'string') {
    if (scope.budget.left <= 0) return undefined;
    const whole = wholeBindingExpr(template);
    if (!whole) return renderBindingText(template, scope);
    scope.budget.left -= 1;
    return evaluateBindingExpr(whole, scope);
  }
  if (template === null || typeof template !== 'object') return template;
  if (depth >= BINDING_VALUE_MAX_DEPTH) return undefined;
  if (Array.isArray(template)) {
    return template.slice(0, BINDING_EACH_MAX_ITEMS).map((entry) => {
      const value = resolveValue(entry, scope, depth + 1);
      return value === undefined ? null : value;
    });
  }
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(template)) {
    const value = resolveValue(entry, scope, depth + 1);
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * Resolves a value template (VALUES above): `"{{item.price}}"` gives the number, `{ sku: "{{item.sku}}",
 * qty: 1 }` an object with its bindings filled. A missing whole binding gives undefined (an object drops
 * the key; an array holds null). Never throws: an unreadable template gives undefined.
 */
export function resolveBindingValue(template: unknown, scope: BindingScope): unknown {
  try {
    return resolveValue(template, withBudget(scope), 0);
  } catch {
    return undefined;
  }
}

// =============================================================================
// REFERENCES (for the validator)
// =============================================================================

/** One path a text template reads, with the `each` levels it sits in. */
export interface BindingReference {
  path: string[];
  eachDepth: number;
  formatters: BindingFormatterCall[];
}

/** Every path a parsed text template reads, `#each` and `#if` paths included. */
export function collectBindingReferences(nodes: readonly BindingNode[], eachDepth = 0): BindingReference[] {
  const out: BindingReference[] = [];
  for (const node of nodes) {
    if (node.t === 'var') out.push({ path: node.expr.path, eachDepth, formatters: node.expr.formatters });
    if (node.t === 'if') {
      out.push({ path: node.path, eachDepth, formatters: [] });
      out.push(...collectBindingReferences(node.then, eachDepth), ...collectBindingReferences(node.else, eachDepth));
    }
    if (node.t === 'each') {
      out.push({ path: node.path, eachDepth, formatters: [] });
      out.push(...collectBindingReferences(node.body, eachDepth + 1));
    }
  }
  return out;
}

/** How many `{{#each}}` levels a parsed text template nests at its deepest. */
export function bindingTextEachDepth(nodes: readonly BindingNode[]): number {
  let max = 0;
  for (const node of nodes) {
    if (node.t === 'each') max = Math.max(max, 1 + bindingTextEachDepth(node.body));
    if (node.t === 'if') max = Math.max(max, bindingTextEachDepth(node.then), bindingTextEachDepth(node.else));
  }
  return max;
}
